import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { MerchantImportDialog } from './MerchantImportDialog';
import { GoogleSheetSyncDialog } from './GoogleSheetSyncDialog';
import { DuplicateMerchantsDialog } from './DuplicateMerchantsDialog';
import { ExportListButtons } from './ExportListButtons';
import { Store, Search, CheckCircle, XCircle, Clock } from 'lucide-react';
import { MerchantsList } from './admin-merchants/MerchantsList';
import { MerchantDetailDialog } from './admin-merchants/MerchantDetailDialog';
import { RejectReasonDialog, EditMerchantDialog, DeleteMerchantDialog } from './admin-merchants/MerchantActionDialogs';
import { FILTER_ACTIVE_STYLES, FILTER_INACTIVE } from './admin-merchants/constants';

export function AdminMerchantsPage() {
  const { api } = useAuth();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [editForm, setEditForm] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [limit, setLimit] = useState(15);

  useEffect(() => {
    api.get('/api/marketplace/industries')
      .then(({ data }) => setIndustries(data.industries || []))
      .catch(() => {});
  }, [api]);

  const fetchMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit, status: statusFilter });
      if (search) params.append('search', search);
      const { data } = await api.get(`/api/admin/merchants?${params}`);
      setMerchants(data.merchants || []);
      setTotal(data.total || 0);
      setCounts(data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (e) {
      toast.error('Failed to load merchants');
    }
    setLoading(false);
  }, [api, page, limit, statusFilter, search]);

  useEffect(() => { fetchMerchants(); }, [fetchMerchants]);

  // Clear bulk selection whenever the underlying list changes so the user
  // doesn't accidentally bulk-approve invisible rows.
  useEffect(() => { setSelectedIds([]); }, [statusFilter, search, page]);

  const toggleSelect = (id) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const selectAllPending = () => {
    const pendingIds = merchants.filter((m) => m.merchant_status === 'pending').map((m) => m.id);
    setSelectedIds(pendingIds);
  };

  const clearSelection = () => setSelectedIds([]);

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Approve ${selectedIds.length} merchant ${selectedIds.length === 1 ? 'application' : 'applications'}? This sends approval notifications + emails.`)) return;
    setBulkApproving(true);
    try {
      const res = await api.post('/api/admin/merchants/bulk-approve', { merchant_ids: selectedIds });
      const { approved_count = 0, skipped = [], failed = [] } = res.data || {};
      if (approved_count > 0) toast.success(`Approved ${approved_count} merchant${approved_count === 1 ? '' : 's'}`);
      if (skipped.length > 0) toast.info(`${skipped.length} skipped (already approved or not found)`);
      if (failed.length > 0) {
        toast.error(`${failed.length} failed — see console for details`);
        console.warn('bulk-approve failures:', failed);
      }
      setSelectedIds([]);
      fetchMerchants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setBulkApproving(false);
    }
  };

  const openDetail = (m) => { setSelectedMerchant(m); setDetailOpen(true); };

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      await api.post(`/api/admin/merchants/${id}/approve`);
      toast.success('Merchant approved');
      setDetailOpen(false);
      fetchMerchants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!selectedMerchant) return;
    setActionLoading(true);
    try {
      await api.post(`/api/admin/merchants/${selectedMerchant.id}/reject`, { reason: rejectReason || 'Application did not meet requirements.' });
      toast.success('Merchant rejected');
      setRejectOpen(false);
      setDetailOpen(false);
      setRejectReason('');
      fetchMerchants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    }
    setActionLoading(false);
  };

  const openEdit = () => {
    if (!selectedMerchant) return;
    const info = selectedMerchant.merchant_info || {};
    setEditForm({
      name: selectedMerchant.name || '',
      email: selectedMerchant.email || '',
      mobile: selectedMerchant.mobile || '',
      business_name: info.business_name || '',
      industry_id: info.industry_id || '',
      business_address: info.business_address || '',
      business_description: info.business_description || '',
      business_logo_url: info.business_logo_url || '',
    });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    setActionLoading(true);
    try {
      await api.put(`/api/admin/merchants/${selectedMerchant.id}`, editForm);
      toast.success('Merchant updated');
      setEditOpen(false);
      setDetailOpen(false);
      fetchMerchants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
    setActionLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteReason.trim()) { toast.error('Provide a deletion reason'); return; }
    setActionLoading(true);
    try {
      const res = await api.delete(`/api/admin/merchants/${selectedMerchant.id}`, { data: { reason: deleteReason } });
      toast.success(`Merchant deleted. ${res.data.services_deactivated || 0} listings deactivated.`);
      setDeleteOpen(false);
      setDetailOpen(false);
      setDeleteReason('');
      fetchMerchants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
    setActionLoading(false);
  };

  const skip = (page - 1) * limit;

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="admin-merchants-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-white flex items-center gap-2">
              <Store className="w-6 h-6 text-[#00E676]" /> Merchant Management
            </h1>
            <p className="text-[#A0B4CC] text-sm mt-1">Review and manage merchant applications</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportListButtons api={api} resource="merchants" filename="merchants" />
            <DuplicateMerchantsDialog api={api} />
            <GoogleSheetSyncDialog api={api} onSynced={fetchMerchants} />
            <MerchantImportDialog api={api} urlBase="/api/admin/merchants/import" onImported={fetchMerchants} testIdPrefix="admin-import" />
          </div>
        </div>

        {/* Stats Pills */}
        <div className="flex flex-wrap gap-3 mb-6">
          {[
            { key: 'pending', label: 'Pending', icon: Clock, color: 'amber' },
            { key: 'approved', label: 'Approved', icon: CheckCircle, color: 'emerald' },
            { key: 'rejected', label: 'Rejected', icon: XCircle, color: 'red' },
            { key: 'all', label: 'All', icon: Store, color: 'blue' },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => { setStatusFilter(s.key); setPage(1); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                statusFilter === s.key ? (FILTER_ACTIVE_STYLES[s.color] || FILTER_ACTIVE_STYLES.blue) : FILTER_INACTIVE
              }`}
              data-testid={`filter-${s.key}`}
            >
              <s.icon className="w-3.5 h-3.5" />
              {s.label}
              {s.key !== 'all' && <span className="ml-1 text-xs opacity-70">({counts[s.key] || 0})</span>}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
          <Input
            placeholder="Search by name, email, or business..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10 bg-[#041A3B] border-white/10 text-white"
            data-testid="merchant-search"
          />
        </div>

        <MerchantsList
          merchants={merchants}
          loading={loading}
          statusFilter={statusFilter}
          selectedIds={selectedIds}
          bulkApproving={bulkApproving}
          toggleSelect={toggleSelect}
          selectAllPending={selectAllPending}
          clearSelection={clearSelection}
          handleBulkApprove={handleBulkApprove}
          openDetail={openDetail}
          skip={skip}
          limit={limit}
          total={total}
          onPageChange={(newSkip) => setPage(Math.floor(newSkip / limit) + 1)}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
        />

        <MerchantDetailDialog
          open={detailOpen}
          onOpenChange={setDetailOpen}
          merchant={selectedMerchant}
          api={api}
          actionLoading={actionLoading}
          onApprove={handleApprove}
          onAskReject={() => setRejectOpen(true)}
          onAskEdit={openEdit}
          onAskDelete={() => setDeleteOpen(true)}
        />

        <RejectReasonDialog
          open={rejectOpen}
          onOpenChange={setRejectOpen}
          reason={rejectReason}
          setReason={setRejectReason}
          onConfirm={handleReject}
          actionLoading={actionLoading}
        />

        <EditMerchantDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          form={editForm}
          setForm={setEditForm}
          industries={industries}
          onSave={handleEdit}
          actionLoading={actionLoading}
          api={api}
        />

        <DeleteMerchantDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          reason={deleteReason}
          setReason={setDeleteReason}
          onConfirm={handleDelete}
          actionLoading={actionLoading}
        />
      </div>
    </AdminSidebarLayout>
  );
}

export default AdminMerchantsPage;
